/**
 * Money — PROJECT-OVERVIEW §5.6, BR-04, BR-21, BR-27.
 *
 * ⛔ **THERE IS NO CONVERSION FUNCTION IN THIS MODULE, AND THERE MUST NEVER BE ONE
 * — not even a private one.**
 *
 * BR-21 is, in the words of the overview itself, *"the rule most likely to be
 * broken"*: a period's values must use the contract parameters **in force at the
 * time**, never today's PTAX or today's discount. A contract can be amended
 * mid-term, and past periods keep the old parameter set.
 *
 * The cheapest possible defence is for the formatting layer to **not offer the
 * operation**. A screen that needs BRL asks the backend for BRL — the backend is
 * the only place that knows which parameters applied to which period. A helper
 * here called `converterParaBrl(valor, ptax)` would be used within a week, would
 * look completely reasonable in review, and would be wrong for every historical
 * period on the platform.
 *
 * `formatoDeMoeda.test.ts` greps this directory for `convert`/`converter` next to
 * a currency and fails the suite. That guard is deliberately crude: a false
 * positive costs a rename, a false negative costs a wrong number in front of a
 * court.
 */

/** The two currencies the platform holds. USD comes from AWS/CUDOS; BRL from the
 *  backend, already converted through the period's contract parameters. */
export type Moeda = 'USD' | 'BRL'

/**
 * 🔒 **Every money formatter takes a currency and prints its symbol.** §5.6: costs
 * exist in both and the UI must **always label which**. There is deliberately no
 * `formatarDinheiro(valor)` overload that omits it — an unlabelled figure on a
 * screen showing two currencies is worse than no figure, because it looks correct.
 */
export function formatarMoeda(valor: number, moeda: Moeda): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: moeda,
    // AWS bills to the cent; a rounded total that does not match the invoice is a
    // support ticket.
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valor)
}

/** `US$ 1.895,00` — costs as AWS and CUDOS report them. */
export function formatarUsd(valor: number): string {
  return formatarMoeda(valor, 'USD')
}

/** `R$ 9.475,00` — already converted BY THE BACKEND, with the period's parameters. */
export function formatarBrl(valor: number): string {
  return formatarMoeda(valor, 'BRL')
}

/**
 * A monthly opportunity value — AC-6.
 *
 * All five value fields of `Oportunidade` are monthly, and Eduardo was explicit
 * that the label must say so: *"é sempre mensal... é o valor mensal que tem que
 * estar no texto ali."* A number on that screen without `/mês` reads as a total,
 * and the difference between "saves R$ 2.410" and "saves R$ 2.410 a month" is a
 * factor of twelve in a commercial conversation.
 */
export function formatarValorMensal(valor: number, moeda: Moeda): string {
  return `${formatarMoeda(valor, moeda)}/mês`
}

/**
 * The same value written out, for places where the compact `/mês` is too terse —
 * a card subtitle, a tooltip, an exported report.
 */
export function formatarValorMensalPorExtenso(valor: number, moeda: Moeda): string {
  return `${formatarMoeda(valor, moeda)} por mês`
}
